import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { DefaultButton } from "./DefaultButton";
import { generateTextShadow } from "@/lib/utils";
import { ChevronsLeftRightEllipsis } from "lucide-react";

type LnaPermissionDialogProps = {
  open: boolean;
  mode: "prompt" | "denied";
  onContinueToAllow: () => void;
  onDismiss: () => void;
};

export function LnaPermissionDialog({
  open,
  mode,
  onContinueToAllow,
  onDismiss,
}: LnaPermissionDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onDismiss();
      }}
    >
      <DialogContent
        className="!clamp-[max-w,xs,22.5rem] bg-[#1A3227] flex flex-col items-center rounded-3xl gap-0 border-3"
        style={{
          boxShadow: "inset 0 3px 0 #2C4439, inset 0 -5px 0 #0C1C16",
        }}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {mode === "prompt" ? (
          <PromptContent onContinueToAllow={onContinueToAllow} />
        ) : (
          <DeniedContent onDismiss={onDismiss} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PromptContent({
  onContinueToAllow,
}: {
  onContinueToAllow: () => void;
}) {
  return (
    <>
      <DialogHeader className="w-full px-4 border-0 flex flex-col items-center mt-4">
        <div className="size-16 rounded-full bg-black/25 flex items-center justify-center">
          <ChevronsLeftRightEllipsis className="text-white size-8" />
        </div>
      </DialogHeader>
      <div className="px-4 pb-6 flex flex-col items-center">
        <div className="flex flex-col items-center mx-2 text-white font-bold text-center gap-2">
          <DialogTitle
            className="text-xl tracking-wide uppercase"
            style={{ textShadow: generateTextShadow(1.5) }}
          >
            Allow Connections to Your Wallet
          </DialogTitle>
          <DialogDescription
            className="text-sm text-white/80 font-normal"
            style={{ textShadow: generateTextShadow(1.5) }}
          >
            To get started, allow CFL to connect to your Seeker wallet over your
            local network. This lets the app securely communicate with your
            wallet.
          </DialogDescription>
        </div>
        <DefaultButton
          variant="green"
          className="!clamp-[h,12,14] min-w-[160px] px-4! mt-6 uppercase"
          onClick={onContinueToAllow}
        >
          <span className="text-white font-bold clamp-[text,sm,base] -mt-1">
            Continue to Allow
          </span>
        </DefaultButton>
        <p className="text-xs text-white/50 mt-3 text-center">
          You’ll see a system prompt to confirm.
        </p>
        <p className="text-xs text-white/50 text-center">
          Without it, your wallet won’t work in CFL.
        </p>
      </div>
    </>
  );
}

function DeniedContent({ onDismiss }: { onDismiss: () => void }) {
  return (
    <>
      <DialogHeader className="w-full px-4 border-0 flex flex-col items-center mt-4">
        <div className="size-16 rounded-full bg-[#3A2222] flex items-center justify-center">
          <BlockedIcon />
        </div>
      </DialogHeader>
      <div className="px-4 pb-6 flex flex-col items-center">
        <div className="flex flex-col items-center mx-2 text-white font-bold text-center gap-2">
          <DialogTitle
            className="text-xl tracking-wide uppercase"
            style={{ textShadow: generateTextShadow(1.5) }}
          >
            Permission Required
          </DialogTitle>
          <DialogDescription
            className="text-sm text-white/80 font-normal"
            style={{ textShadow: generateTextShadow(1.5) }}
          >
            CFL can’t connect to your Seeker Wallet because permission was
            denied.
            <br />
            <div className="text-left bg-black/25 border rounded-lg px-4 py-2 mt-4">
              <p>To fix this:</p>
              <ul className="ml-2 my-1">
                <li>1. Hold the CFL app icon</li>
                <li>
                  2. Open <span className="text-teal-400">"Site Settings"</span>
                </li>
                <li>
                  3. Tap{" "}
                  <span className="text-teal-400">
                    "Delete data & reset permissions"
                  </span>{" "}
                </li>
              </ul>
              <p>Then reopen the app and sign in again.</p>
            </div>
            <p className="mt-4">
              Still stuck? reach us out on{" "}
              <a
                href="https://discord.com/invite/GQuP7ekfF9"
                className="underline"
              >
                discord
              </a>
            </p>
          </DialogDescription>
        </div>
        <DefaultButton
          variant="default"
          className="!clamp-[h,12,14] min-w-[120px] px-4! mt-6"
          onClick={onDismiss}
        >
          <span className="text-white font-bold clamp-[text,sm,base] -mt-1">
            OK
          </span>
        </DefaultButton>
      </div>
    </>
  );
}

function BlockedIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-red-400"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m4.9 4.9 14.2 14.2" />
    </svg>
  );
}
